import React from "react";

/**
 * Formats a raw cell value for display, using the field's declared type so
 * MySQL's 0/1 booleans read as words rather than digits.
 * @param {unknown} value the raw value.
 * @param {string} [type] the field's logical type.
 * @returns {string}
 */
function formatCell(value, type) {
  if (value === null || value === undefined || value === "") return "—";
  if (type === "boolean") return Number(value) ? "Yes" : "No";
  return String(value);
}

/**
 * A single preview row. Memoized so paging/re-render stays cheap.
 */
const PreviewRow = React.memo(function PreviewRow({ row, columns }) {
  return (
    <tr>
      {columns.map((col) => (
        <td key={col.key} className="qb-td">
          {formatCell(row[col.key], col.type)}
        </td>
      ))}
    </tr>
  );
});

/**
 * Read-only table of preview rows. Rows are ephemeral query output (no edit
 * state), so an index key is acceptable here.
 * @param {object} props
 * @param {Array<Record<string, unknown>>} props.rows the page of rows.
 * @param {Array<{key: string, label: string, type?: string}>} props.columns the
 *   columns to render, carrying the field's display label and type.
 * @returns {JSX.Element}
 */
export default function PreviewTable({ rows, columns }) {
  if (!rows.length) {
    return <p className="qb-muted">No rows match this query.</p>;
  }
  return (
    <div className="qb-table-wrap">
      <table className="qb-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} className="qb-th">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <PreviewRow key={index} row={row} columns={columns} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
